import type { AlreadyPurchased } from "./AlreadyPurchased";
import type { AvailableForPurchase } from "./AvailableForPurchase";
import type { ComponentInfo } from "./ComponentInfo";
import type { NotAvailableForPurchase } from "./NotAvailableForPurchase";

export type ProductOptionStatus = ComponentInfo & (AlreadyPurchased | AvailableForPurchase | NotAvailableForPurchase);
export type PurchaseStatus = Record<string, ProductOptionStatus>;

// Runtime validation for ProductOptionStatus
export const ProductOptionStatus = {
  validate: (data: unknown): { success: true; value: ProductOptionStatus } | { success: false; message: string } => {
    if (!data || typeof data !== "object") {
      return { success: false, message: "Invalid ProductOptionStatus: not an object" };
    }

    const obj = data as Record<string, unknown>;

    if (typeof obj.componentName !== "string" && obj.componentName !== undefined) {
      return { success: false, message: "Invalid ProductOptionStatus: componentName must be a string or undefined" };
    }

    if (typeof obj.componentDescription !== "string" && obj.componentDescription !== undefined) {
      return { success: false, message: "Invalid ProductOptionStatus: componentDescription must be a string or undefined" };
    }

    if (obj.status === "purchased") {
      if (obj.downloadUrl !== null && typeof obj.downloadUrl !== "string") {
        return { success: false, message: "Invalid ProductOptionStatus: downloadUrl must be a string or null" };
      }
    } else {
      if (typeof obj.price !== "string") {
        return { success: false, message: "Invalid ProductOptionStatus: price must be a string" };
      }

      if (obj.originalPrice !== null && typeof obj.originalPrice !== "string") {
        return { success: false, message: "Invalid ProductOptionStatus: originalPrice must be a string or null" };
      }

      if (typeof obj.requiresPaymentMethod !== "boolean") {
        return { success: false, message: "Invalid ProductOptionStatus: requiresPaymentMethod must be a boolean" };
      }
    }

    return { success: true, value: obj as ProductOptionStatus };
  },

  guard: (data: unknown): data is ProductOptionStatus => {
    const result = ProductOptionStatus.validate(data);
    return result.success;
  },
};

// Runtime validation for PurchaseStatus
export const PurchaseStatus = {
  validate: (data: unknown): { success: true; value: PurchaseStatus } | { success: false; message: string } => {
    if (!data || typeof data !== "object") {
      return { success: false, message: "Invalid PurchaseStatus: not an object" };
    }

    const obj = data as Record<string, unknown>;
    const result: PurchaseStatus = {};

    for (const [key, value] of Object.entries(obj)) {
      const optionValidation = ProductOptionStatus.validate(value);
      if (!optionValidation.success) {
        return { success: false, message: `Invalid option '${key}': ${optionValidation.message}` };
      }
      result[key] = optionValidation.value;
    }

    return { success: true, value: result };
  },

  guard: (data: unknown): data is PurchaseStatus => {
    const result = PurchaseStatus.validate(data);
    return result.success;
  },
};
